import {
  ApplicationStatus,
  VerificationNames,
  VerificationStatus,
  Invitee
} from "shared/types";
import { ApplicantOrInvitee, Verification } from "../types";
import { sortVerifications } from "views/applications-id/components/ApplicantsTable/utils";
import {
  HIDDEN_VERIFICATIONS,
  VERIFICATIONS_ORDER
} from "views/applications-id/components/ApplicantsTable/constants";
import { Link } from "react-router-dom";
import { ApplicantHeaderCell } from "views/applications-id/components/ApplicantsTable/components/ApplicantHeaderCell";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { combineVerifications, has } from "shared/utils";
import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { CreditReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/CreditReportSection";
import { CriminalReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/CriminalReportSection";
import { HousingHistorySection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/HousingHistorySection";
import { IncomeReportSection } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/IncomeReportSection";
import { EvictionReportCell } from "./TableBodySections/EvictionReportCell";
import { IdentityVerificationCell } from "./TableBodySections/IdentityVerificationCell";
import { RentalApplicationCell } from "./TableBodySections/RentalApplicationCell";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { RequiresOverridesIdentityVerification } from "shared/types";
import { CanadianCriminalReportStatus } from "views/applications-id/components/ApplicantsTable/components/CanadianCriminalReportStatus";
import { ApplyDiscountModal } from "views/discounts/components/ApplyDiscountModal";
import {
  useApplyDiscountToApplicant,
  useRemoveDiscountFromApplicant,
  useDiscounts
} from "views/discounts/hooks/useDiscounts";
import { DiscountCode } from "views/discounts/types";
import { PetScreeningReportSection } from "./TableBodySections/PetScreeningReportSection";
import { ApplicationStartedCell } from "./TableBodySections/ApplicationStartedCell";
import { ApplicationSubmittedCell } from "./TableBodySections/ApplicationSubmittedCell";
import { ApplicationFeeCell } from "./TableBodySections/ApplicationFeeCell";
import { CombinedIncomeReportSection } from "./TableBodySections/CombinedIncomeReportSection";
import { findIntersectionLeftovers } from "utils/arrayUtils";
import { mergeHousingHistoryVerifications } from "views/applications-id-report/utils/mergeHousingHistoryVerifications";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";
import { ApplicationStatusCell } from "./TableBodySections/ApplicationStatusCell";
import { toast } from "react-toastify";
import { FeeCell } from "./TableBodySections/FeeCell";

interface TableBodyProps {
  applicants: ApplicantOrInvitee[];
  verificationNames: VerificationNames[];
  ruleBased?: boolean;
  scoreBased?: boolean;
  hasClearaCategorization?: boolean;
  applicationStatus?: ApplicationStatus;
  applicationId?: string;
  pendingMagicLinks?: Invitee[];
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setResendInviteModal: Dispatch<SetStateAction<Invitee | undefined>>;
  verificationSchemas?: VerificationSchemaNames;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  setManualApplicationOpen: (applicantId: number | undefined) => void;
  templateId?: string;
  hasAdministrationFee: boolean;
  hasHoldingFee: boolean;
}
export const TableBody = ({
  ruleBased,
  scoreBased,
  applicants,
  verificationNames,
  verificationSchemas,
  hasClearaCategorization,
  applicationStatus,
  applicationId,
  pendingMagicLinks,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setRefundModal,
  setResendInviteModal,
  allowCoSigner,
  allowOccupant,
  requiresOverridesIdentityVerification,
  setManualApplicationOpen,
  templateId,
  hasAdministrationFee,
  hasHoldingFee
}: TableBodyProps) => {
  const { permissions, partner } = useAuthContext();

  const premiumRentalVerificationEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_PREMIUM_RENTAL_VERIFICATION) ??
    false;
  const [discountModal, setDiscountModal] = useState<{
    email: string;
    id: number;
  } | null>(null);
  const { data: activeDiscounts = [] } = useDiscounts({
    active: true,
    restriction_type: "template",
    template_id: templateId
  });
  const applyDiscountMutation = useApplyDiscountToApplicant();
  const removeDiscountMutation = useRemoveDiscountFromApplicant();
  const handleApplyDiscount = (
    discount:
      | DiscountCode
      | {
          isCustom: true;
          code: string;
          discount_type: string;
          discount_amount: number;
          end_date?: string;
        }
  ) => {
    setDiscountModal(null);
    if (!applicationId || !discountModal) return;

    const applicant = applicants.find((app) => app.id === discountModal.id);

    if (!applicant) return toast.error("No applicant or invitee");

    const isApplicantEntity = "verifications" in applicant;

    applyDiscountMutation.mutate({
      applicationId: Number(applicationId),
      applicantId: discountModal.id,
      discountData:
        "isCustom" in discount
          ? {
              discount_code: discount.code,
              custom_discount_type: discount.discount_type,
              custom_discount_amount: discount.discount_amount,
              custom_end_date: discount.end_date
            }
          : { discount_code: discount.code },
      isApplicantEntity
    });
  };
  const handleCancelDiscount = (applicantId: number) => {
    if (!applicationId) return;

    const applicant = applicants.find((app) => app.id === applicantId);

    if (!applicant) return toast.error("No applicant or invitee");

    const isApplicantEntity = "verifications" in applicant;

    removeDiscountMutation.mutate({
      applicationId: Number(applicationId),
      applicantId,
      isApplicantEntity
    });
  };

  const applicantsWithBlankVerifications = applicants.map((applicant) => {
    const isApplicant = "verifications" in applicant;
    if (!isApplicant)
      return {
        original: applicant,
        ...applicant,
        total_additional_documents: 0,
        verifications: sortVerifications(
          verificationNames.map((name) => {
            return {
              id: `missing-${name}`,
              name: name as VerificationNames,
              status: "pending"
            };
          }) as unknown as Verification[],
          VERIFICATIONS_ORDER
        )
      };
    // @ts-expect-error -- man...
    applicant.is_submission_disclosure_finished = Boolean(
      applicant.verifications?.find(
        (v) => v.name === VerificationNames.SubmissionDisclosure
      )?.status === "finished"
    );
    const applicantVerifications = mergeHousingHistoryVerifications(
      applicant.verifications?.filter(
        (v) => !HIDDEN_VERIFICATIONS.includes(v.name as VerificationNames)
      ) || [],
      premiumRentalVerificationEnabled
    );
    const missingVerificationNames = findIntersectionLeftovers(
      verificationNames,
      applicantVerifications.map((v) => v.name)
    );

    if (
      !missingVerificationNames.length &&
      applicantVerifications.length === verificationNames.length
    )
      return {
        original: applicant,
        ...applicant,
        total_additional_documents: applicant.total_additional_documents,
        verifications: sortVerifications(
          applicantVerifications,
          VERIFICATIONS_ORDER
        )
      };
    // Create new verifications array with missing verifications and status "pending"
    const newVerifications = missingVerificationNames.map((name) => {
      return {
        id: `missing-${name}`,
        name,
        status: VerificationStatus.NoDataAvailable
      };
    });
    // Return new applicant object with new verifications array sorted by VERIFICATIONS_ORDER
    return {
      original: applicant,
      ...applicant,
      verifications: sortVerifications(
        [...(applicantVerifications || []), ...newVerifications],
        VERIFICATIONS_ORDER
      )
    };
  });
  return (
    <>
      {applicantsWithBlankVerifications.map((applicant) => {
        const isApplicant = "verifications" in applicant.original;
        // the collision of IDs is possible when link and applicant have the same ID. We assume that there > links than applicants.
        const pendingMagicLink = pendingMagicLinks?.find(
          (link) => link.id === applicant.id
        );
        const hasAuditFlagEnabled = Object.values(
          ("audit_summary_flags" in applicant &&
            typeof applicant.audit_summary_flags === "object" &&
            applicant.audit_summary_flags) ||
            {}
        ).some((value) => value);
        const isIdentityVerificationOverrideRequired =
          requiresOverridesIdentityVerification?.applicants.some(
            ({ id }) => id === applicant.id
          );

        const rowContent = (
          <>
            <ApplicantHeaderCell
              allowCoSigner={allowCoSigner}
              allowOccupant={allowOccupant}
              setCancelApplicantModal={setCancelApplicantModal}
              setUncancelApplicantModal={setUncancelApplicantModal}
              setChangeRoleModal={setChangeRoleModal}
              setRefundModal={setRefundModal}
              setResendInviteModal={setResendInviteModal}
              applicant={applicant.original}
              applicationStatus={applicationStatus}
              applicationId={applicationId}
              pendingMagicLink={pendingMagicLink}
              setManualApplicationOpen={(v) => {
                setManualApplicationOpen(v);
              }}
            />
            <ApplicationStatusCell
              applicant={applicant.original}
              pendingMagicLink={pendingMagicLink}
            />
            <ApplicationStartedCell applicant={applicant.original} />
            <ApplicationSubmittedCell applicant={applicant.original} />
            <ApplicationFeeCell
              applicant={applicant.original}
              setDiscountModal={setDiscountModal}
              handleCancelDiscount={handleCancelDiscount}
              isActiveDiscounts={!!activeDiscounts.length}
              alwaysAllowCustomDiscounts={true}
            />
            {hasAdministrationFee && (
              <FeeCell
                applicant={applicant.original}
                feeType="administration_fee"
              />
            )}
            {hasHoldingFee && (
              <FeeCell applicant={applicant.original} feeType="holding_fee" />
            )}
            {/* @ts-expect-error */}
            {combineVerifications(applicant.verifications, [
              [
                VerificationNames.IncomeVerification,
                VerificationNames.VerifiedIncomeVerification,
                VerificationNames.CombinedIncome
              ]
            ]).map((v) => {
              if (Array.isArray(v)) {
                const combinedIncomeV = v.find(
                  (incomeV) =>
                    incomeV.name === VerificationNames.CombinedIncome &&
                    incomeV.status !== VerificationStatus.NoDataAvailable
                );
                return combinedIncomeV ? (
                  <CombinedIncomeReportSection
                    applicant={applicant.original}
                    verification={combinedIncomeV}
                    hasAuditFlagEnabled={hasAuditFlagEnabled}
                  />
                ) : (
                  <IncomeReportSection
                    key={v[0].id}
                    applicant={applicant.original}
                    verifications={v}
                    hasAuditFlagEnabled={hasAuditFlagEnabled}
                  />
                );
              }

              const inProgress =
                [
                  VerificationStatus.Pending,
                  VerificationStatus.Canceled,
                  VerificationStatus.Processing,
                  VerificationStatus.Started
                ].includes(v.status) ||
                applicationStatus === ApplicationStatus.Canceled;
              return (
                <Fragment key={v.id}>
                  {/* Empty cell for all rows */}
                  <Cell className="bg-background-900" />

                  {v.name === VerificationNames.RentalApplication ? (
                    <RentalApplicationCell verification={v} />
                  ) : null}
                  {v.name === VerificationNames.IdentityVerification ? (
                    <IdentityVerificationCell
                      verification={v}
                      isIdentityVerificationOverrideRequired={
                        isIdentityVerificationOverrideRequired
                      }
                    />
                  ) : null}
                  {v.name === VerificationNames.HousingHistory ? (
                    <HousingHistorySection
                      applicant={applicant.original}
                      verification={v}
                      verificationSchemas={verificationSchemas}
                      hasAuditFlagEnabled={hasAuditFlagEnabled}
                    />
                  ) : null}
                  {v.name === VerificationNames.EvictionReport ? (
                    <EvictionReportCell
                      verification={v}
                      applicant={applicant.original}
                      hasAuditFlagEnabled={hasAuditFlagEnabled}
                    />
                  ) : null}
                  {v.name === VerificationNames.CreditReport ? (
                    <CreditReportSection
                      scoreBased={scoreBased}
                      applicant={applicant.original}
                      verification={v}
                      ruleBased={ruleBased}
                      inProgress={inProgress}
                      isApplicant={isApplicant}
                      hasAuditFlagEnabled={hasAuditFlagEnabled}
                    />
                  ) : null}
                  {v.name === VerificationNames.CriminalReport ? (
                    <CriminalReportSection
                      hasClearaCategorization={hasClearaCategorization}
                      hasAuditFlagEnabled={hasAuditFlagEnabled}
                      verification={v}
                      applicant={applicant.original}
                      ruleBased={ruleBased}
                      inProgress={inProgress}
                      isApplicant={isApplicant}
                    />
                  ) : null}
                  {v.name === VerificationNames.CanadianCriminalReport ? (
                    <Cell
                      isDataCell
                      className="relative"
                      isPermitted={
                        permissions.canAccessReports.caCriminalReport
                      }
                      showRestrictedFlag={
                        !permissions.canAccessReports.caCriminalReport
                      }
                    >
                      {has(v.status) ? (
                        <CanadianCriminalReportStatus status={v.status} />
                      ) : (
                        "-"
                      )}
                    </Cell>
                  ) : null}
                  {v.name === VerificationNames.PetScreeningReport ? (
                    <PetScreeningReportSection
                      verification={v}
                      applicant={applicant.original}
                    />
                  ) : null}
                </Fragment>
              );
            })}
            {/* Empty cell for additional document spacer */}
            <Cell className="bg-background-900" />
            <Cell
              isDataCell
              isPermitted={
                permissions.canAccessReports.additionalDocumentsReport
              }
              showRestrictedFlag={
                !permissions.canAccessReports.additionalDocumentsReport
              }
            >
              {isApplicant ? applicant.total_additional_documents : "-"}
            </Cell>
          </>
        );

        const key = applicant.email + applicant.id + applicant + isApplicant;

        return (
          <div key={key} className="divide-y divide-alpha-40 group">
            {isApplicant && "id" in applicant ? (
              <Link
                to={`/applications/${applicationId}/reports?applicantId=${applicant.id}`}
                className="contents"
              >
                {rowContent}
              </Link>
            ) : (
              rowContent
            )}
          </div>
        );
      })}
      {discountModal ? (
        <ApplyDiscountModal
          opened={true}
          onClose={() => setDiscountModal(null)}
          applicantEmail={discountModal.email}
          activeDiscounts={activeDiscounts}
          onApplyDiscount={handleApplyDiscount}
        />
      ) : null}
    </>
  );
};
